/**
 * Shared browser image cache for office sprites.
 * Mirrors wall-art loading in world-engine: lazy, fail-open, no retry storms.
 */

export type SpriteLoadStatus = "missing" | "loading" | "ready" | "failed";

type CacheEntry =
  | { status: "loading" }
  | { status: "ready"; img: HTMLImageElement }
  | { status: "failed" };

const cache = new Map<string, CacheEntry>();
/** One-shot callbacks fired when a currently-loading src becomes ready. */
const waiters = new Map<string, Array<() => void>>();

function canUseDomImage(): boolean {
  return typeof Image !== "undefined";
}

/**
 * Returns the image only when fully loaded; otherwise starts a load and returns null.
 * Failed srcs stay failed for the session (no per-frame re-fetch).
 *
 * `onReady` (optional) fires ONCE if/when an in-flight load succeeds — used by the
 * world engine to re-bake the floor after ground-texture PNGs finish loading, so a
 * cold cache no longer leaves the plaza/office floor on flat fallback fills.
 */
export function ensureSprite(src: string, onReady?: () => void): HTMLImageElement | null {
  if (!src) return null;

  const hit = cache.get(src);
  if (hit?.status === "ready") return hit.img;
  if (hit?.status === "failed") return null;
  if (hit?.status === "loading") {
    if (onReady) {
      const list = waiters.get(src);
      if (list) list.push(onReady);
      else waiters.set(src, [onReady]);
    }
    return null;
  }

  if (!canUseDomImage()) {
    cache.set(src, { status: "failed" });
    return null;
  }

  cache.set(src, { status: "loading" });
  if (onReady) waiters.set(src, [onReady]);
  const img = new Image();
  img.decoding = "async";
  img.onload = () => {
    if (img.naturalWidth > 0) {
      cache.set(src, { status: "ready", img });
      const list = waiters.get(src);
      if (list) {
        waiters.delete(src);
        for (const cb of list) cb();
      }
    } else {
      cache.set(src, { status: "failed" });
      waiters.delete(src);
    }
  };
  img.onerror = () => {
    cache.set(src, { status: "failed" });
    waiters.delete(src);
  };
  img.src = src;
  return null;
}

export function getSpriteStatus(src: string): SpriteLoadStatus {
  if (!src) return "missing";
  const hit = cache.get(src);
  if (!hit) return "missing";
  return hit.status;
}

/** Kick off loads for many srcs without blocking. Dedupes via ensureSprite. */
export function warmSprites(srcs: readonly string[]): void {
  for (const src of srcs) {
    if (!src) continue;
    ensureSprite(src);
  }
}

/** Test helper — clears the module cache. */
export function __resetSpriteCacheForTests(): void {
  cache.clear();
  waiters.clear();
}

/** Test helper — inject a ready or failed entry without network. */
export function __setSpriteCacheForTests(
  src: string,
  entry: { status: "ready"; img: HTMLImageElement } | { status: "failed" } | { status: "loading" },
): void {
  cache.set(src, entry);
}
